import { css, cx } from "@microbit/ui";

type Eye = "round" | "tick" | "heart";

type Side = "left" | "right";

interface EmojiProps {
  leftEye?: Eye;
  rightEye?: Eye;
  /** Box size on the spacing scale. */
  size?: "16" | "20";
  className?: string;
}

const Emoji = ({
  leftEye = "round",
  rightEye = "round",
  size = "16",
  className,
}: EmojiProps) => {
  return (
    <svg
      aria-hidden
      viewBox="0 0 124 101"
      className={cx(
        css({ color: "brand.500", flexShrink: 0 }),
        size === "20" ? css({ w: 20, h: 20 }) : css({ w: 16, h: 16 }),
        className
      )}
    >
      {/* Outline */}
      <path
        d="M88.874 0.0159531V0.00657654H34.1453V0.0159531C14.6584 0.173286 0 15.7146 0 36.3026C0 56.6373 14.4073 72.0239 33.6396 72.4399V72.46H88.5953C108.233 72.46 123.041 56.9173 123.041 36.3026C123.041 15.7093 108.371 0.162621 88.874 0.0159531ZM88.5953 57.0106L34.1084 56.9999C23.3745 56.8133 14.726 47.5906 14.726 36.2346C14.726 24.7599 23.5516 15.4586 34.4396 15.4586H88.5953C99.4792 15.4586 108.307 24.7599 108.307 36.2346C108.307 47.7092 99.4792 57.0106 88.5953 57.0106Z"
        fill="currentColor"
      />
      <Eye side="left" type={leftEye} />
      <Eye side="right" type={rightEye} />
      {/* Smile */}
      <path
        d="M61.8344 93.8294C57.7797 93.8294 54.4271 90.7241 53.7677 86.6548H47.5807C48.2287 94.4961 54.1355 100.248 61.8344 100.248C69.5251 100.248 75.4317 94.4961 76.0803 86.6548H69.8969C69.2355 90.7241 65.8812 93.8294 61.8344 93.8294Z"
        fill="currentColor"
      />
    </svg>
  );
};

const Eye = ({ type, side }: { type: Eye; side: Side }) => {
  switch (type) {
    case "round":
      return <RoundEye side={side} />;
    case "tick":
      return <TickEye position={side} />;
    case "heart":
      return <HeartEye side={side} />;
  }
};

const RoundEye = ({ side }: { side: Side }) => {
  return (
    <path
      transform={side === "right" ? "translate(54.2, 0)" : undefined}
      d="M34.3787 28.5268C30.3355 28.5268 27.0609 31.9787 27.0609 36.2347C27.0609 40.488 30.3355 43.9414 34.3787 43.9414C38.4136 43.9414 41.6875 40.488 41.6875 36.2347C41.6875 31.9787 38.4136 28.5268 34.3787 28.5268Z"
      fill="currentColor"
    />
  );
};

const TickEye = ({ position }: { position: Side }) => {
  return (
    <g
      transform={
        position === "left" ? "translate(25, 25)" : "translate(73, 25)"
      }
    >
      <path
        d="M12.132 19.4734L25.0613 5.96014C26.3119 4.64281 26.3119 2.50814 25.0613 1.19081C23.8119 -0.127857 21.7879 -0.127859 20.5359 1.18814C20.5159 1.20947 20.5053 1.23481 20.4853 1.25481L7.66529 14.6548C7.64529 14.6708 7.6226 14.6828 7.60793 14.7041C6.35593 16.0214 6.35727 18.1574 7.60793 19.4734C8.85593 20.7908 10.884 20.7908 12.132 19.4734Z"
        fill="currentColor"
      />
      <path
        d="M12.004 14.6386L6.14 9.19461C4.81067 7.96261 2.78938 8.09731 1.62138 9.49864C0.453381 10.8946 0.581368 13.0266 1.90803 14.26C1.92803 14.276 1.95203 14.2893 1.97203 14.3066L7.72405 19.6453C7.74005 19.6613 7.7547 19.6853 7.7747 19.7026C9.0987 20.9319 11.1213 20.7972 12.2893 19.4012C13.456 18.0026 13.3307 15.8693 12.004 14.6386Z"
        fill="currentColor"
      />
    </g>
  );
};

const HeartEye = ({ side }: { side: Side }) => {
  return (
    <path
      transform={`scale(1.3) ${
        side === "right" ? "translate(20, 20)" : "translate(57, 20)"
      }`}
      d="M7.92396 15.3679L6.81335 14.2013C2.69201 10.3613 0 7.76925 0 4.59325C0 2.00658 1.90399 -0.000113487 4.35599 -0.000113487C5.70666 -0.000113487 7.05329 0.670574 7.92396 1.75457C8.79729 0.670574 10.14 -0.000113487 11.4933 -0.000113487C13.9466 -0.000113487 15.8493 2.00658 15.8493 4.59325C15.8493 7.76925 13.156 10.3613 9.03464 14.2013L7.92396 15.3679Z"
      fill="currentColor"
    />
  );
};

export const EmojiAi = ({ size = "16", className }: EmojiProps) => {
  return (
    <svg
      aria-hidden
      viewBox="0 0 80 80"
      className={cx(
        css({ color: "brand.500", flexShrink: 0 }),
        size === "20" ? css({ w: 20, h: 20 }) : css({ w: 16, h: 16 }),
        className
      )}
    >
      <path
        d="M78.34 28.49C66.34 25.51 65.87 25.39 64.6 8.13999C64.54 7.32999 63.87 6.69999 63.06 6.68999C62.23 6.65999 61.57 7.28999 61.49 8.09999C59.74 25.92 58.84 26.12 47.8 28.48C47.07 28.64 46.56 29.28 46.57 30.03C46.58 30.77 47.12 31.41 47.85 31.54C59.24 33.64 59.32 34.33 61.5 51.95C61.6 52.73 62.26 53.32 63.05 53.32C63.84 53.32 64.5 52.72 64.59 51.94C66.64 34.2 66.9 33.82 78.27 31.54C78.99 31.4 79.51 30.78 79.52 30.05C79.53 29.32 79.05 28.68 78.34 28.5V28.49Z"
        fill="currentColor"
      />
      <path
        d="M44.6 39.18C39.19 37.83 38.98 37.78 38.4 30C38.37 29.64 38.07 29.35 37.71 29.35C37.33 29.34 37.04 29.62 37 29.99C36.21 38.03 35.81 38.12 30.82 39.18C30.49 39.25 30.26 39.54 30.26 39.88C30.26 40.22 30.51 40.5 30.84 40.56C35.98 41.51 36.01 41.82 37 49.77C37.04 50.12 37.34 50.39 37.7 50.39C38.06 50.39 38.36 50.12 38.4 49.77C39.33 41.77 39.44 41.6 44.57 40.57C44.89 40.51 45.13 40.23 45.14 39.9C45.15 39.57 44.93 39.28 44.61 39.2L44.6 39.18Z"
        fill="currentColor"
      />
      <path
        d="M49.8501 14.01C45.8201 13.01 45.6601 12.97 45.2301 7.16999C45.2101 6.89999 44.9901 6.68999 44.7101 6.67999C44.4301 6.67999 44.2101 6.87999 44.1801 7.14999C43.5901 13.14 43.2901 13.2 39.5801 14C39.3401 14.05 39.1601 14.27 39.1701 14.52C39.1701 14.77 39.3501 14.98 39.6001 15.03C43.4301 15.74 43.4501 15.97 44.1901 21.89C44.2201 22.15 44.4501 22.35 44.7101 22.35C44.9801 22.35 45.2001 22.15 45.2301 21.89C45.9201 15.93 46.0101 15.8 49.8301 15.03C50.0701 14.98 50.2501 14.77 50.2501 14.53C50.2501 14.29 50.0901 14.07 49.8501 14.01Z"
        fill="currentColor"
      />
      <path
        d="M45.5 49.07C44.84 52.85 41.56 55.72 37.59 55.72H15.38C11 55.64 7.47998 52.08 7.47998 47.68C7.47998 43.28 11.08 39.64 15.52 39.64H26.53V33.66H15.4C7.45998 33.72 1.47998 39.74 1.47998 47.7C1.47998 55.66 7.34998 61.52 15.19 61.68H37.6C45.14 61.68 50.94 56.34 51.58 49.05H45.51L45.5 49.07Z"
        fill="currentColor"
      />
      <path
        d="M15.5601 50.66C17.2101 50.66 18.5501 49.32 18.5501 47.67C18.5501 46.02 17.2101 44.68 15.5601 44.68C13.9101 44.68 12.5701 46.02 12.5701 47.67C12.5701 49.32 13.9101 50.66 15.5601 50.66Z"
        fill="currentColor"
      />
      <path
        d="M22.96 70.85C20.41 70.85 18.3 68.99 17.88 66.56H13.98C14.39 71.25 18.11 74.68 22.96 74.68C27.81 74.68 31.52 71.24 31.93 66.56H28.04C27.62 68.99 25.51 70.85 22.96 70.85Z"
        fill="currentColor"
      />
    </svg>
  );
};

export default Emoji;
